/**
 * Mock reporting backend.
 *
 * Stands in for `/api/reporting/*` until the warehouse views exist. Everything
 * here is deterministic — a fixed-seed PRNG means the same book of business is
 * produced on every reload, so charts and CSV exports are stable and testable.
 * The whole file is throwaway: `reportingService` is the only consumer.
 */

import type { PolicyStatus, RiskTier } from '../PolicyAdmin/types';
import type {
  PolicyReportRecord,
  ReportingDataResponse,
  ReportingFilters,
  ReportingReferenceData,
  TransactionType,
} from './types';
import { todayStr } from './utils';

const SEED = 20_260_726;
const LATENCY_MS = 420;

/** mulberry32 — small, fast, and repeatable across runs. */
function makeRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4_294_967_296;
  };
}

const CARRIERS = [
  { id: 'ca_parento', name: 'Parento Assurance Company', naic: '10142' },
  { id: 'ca_meridian', name: 'Meridian Life & Disability', naic: '20887' },
  { id: 'ca_cascade', name: 'Cascade Mutual', naic: '31556' },
];

const BROKERS = [
  { id: 'br_gallagher', name: 'Erin Cole', agency: 'Gallagher Benefit Services' },
  { id: 'br_marsh', name: 'Tomás Rivera', agency: 'Marsh McLennan Agency' },
  { id: 'br_lockton', name: 'Hannah Berg', agency: 'Lockton Companies' },
  { id: 'br_hub', name: 'Derek Nwosu', agency: 'HUB International' },
  { id: 'br_nfp', name: 'Alicia Fong', agency: 'NFP Corporate' },
  { id: 'br_woodruff', name: 'Priya Raman', agency: 'Woodruff Sawyer' },
];

const PRODUCTS = [
  'Paid Family & Medical Leave',
  'Parental Leave Top-Up',
  'Short-Term Disability',
  'Supplemental Caregiver Leave',
];

interface InsuredSeed {
  name: string;
  industry: string;
  sicCode: string;
  employeeCount: number;
  city: string;
  state: string;
  postcode: string;
}

const INSUREDS: InsuredSeed[] = [
  {
    name: 'Northwind Robotics, Inc.',
    industry: 'Advanced Manufacturing',
    sicCode: '3559',
    employeeCount: 412,
    city: 'Seattle',
    state: 'WA',
    postcode: '98109',
  },
  {
    name: 'Lumen Health Collective',
    industry: 'Healthcare Services',
    sicCode: '8011',
    employeeCount: 1180,
    city: 'San Francisco',
    state: 'CA',
    postcode: '94108',
  },
  {
    name: 'Atlas Freight Systems',
    industry: 'Logistics & Transport',
    sicCode: '4213',
    employeeCount: 640,
    city: 'Dallas',
    state: 'TX',
    postcode: '75201',
  },
  {
    name: 'Brightleaf Studios',
    industry: 'Media & Entertainment',
    sicCode: '7812',
    employeeCount: 96,
    city: 'New York',
    state: 'NY',
    postcode: '10041',
  },
  {
    name: 'Cedar & Vine Hospitality',
    industry: 'Hospitality',
    sicCode: '5812',
    employeeCount: 288,
    city: 'Denver',
    state: 'CO',
    postcode: '80202',
  },
  {
    name: 'Quanta Financial Group',
    industry: 'Financial Services',
    sicCode: '6199',
    employeeCount: 2040,
    city: 'Boston',
    state: 'MA',
    postcode: '02116',
  },
  {
    name: 'Evergreen Ag Partners',
    industry: 'Agriculture',
    sicCode: '0181',
    employeeCount: 154,
    city: 'Yakima',
    state: 'WA',
    postcode: '98901',
  },
  {
    name: 'Pinnacle Software Labs',
    industry: 'Software',
    sicCode: '7372',
    employeeCount: 520,
    city: 'Austin',
    state: 'TX',
    postcode: '78701',
  },
  {
    name: 'Harbor Point Logistics',
    industry: 'Logistics & Transport',
    sicCode: '4731',
    employeeCount: 372,
    city: 'Baltimore',
    state: 'MD',
    postcode: '21202',
  },
  {
    name: 'Solstice Renewables Corp.',
    industry: 'Energy',
    sicCode: '4911',
    employeeCount: 268,
    city: 'Phoenix',
    state: 'AZ',
    postcode: '85004',
  },
  {
    name: 'Meridian Dental Partners',
    industry: 'Healthcare Services',
    sicCode: '8021',
    employeeCount: 184,
    city: 'Chicago',
    state: 'IL',
    postcode: '60606',
  },
  {
    name: 'Kestrel Aerospace',
    industry: 'Aerospace',
    sicCode: '3728',
    employeeCount: 880,
    city: 'Wichita',
    state: 'KS',
    postcode: '67202',
  },
  {
    name: 'Fieldstone Retail Group',
    industry: 'Retail',
    sicCode: '5311',
    employeeCount: 1540,
    city: 'Columbus',
    state: 'OH',
    postcode: '43215',
  },
  {
    name: 'Verdant Foods Co-op',
    industry: 'Food Production',
    sicCode: '2038',
    employeeCount: 460,
    city: 'Portland',
    state: 'OR',
    postcode: '97204',
  },
  {
    name: 'Sable Creek Mining',
    industry: 'Mining',
    sicCode: '1221',
    employeeCount: 210,
    city: 'Salt Lake City',
    state: 'UT',
    postcode: '84101',
  },
  {
    name: 'Copperline Construction',
    industry: 'Construction',
    sicCode: '1542',
    employeeCount: 340,
    city: 'Nashville',
    state: 'TN',
    postcode: '37203',
  },
  {
    name: 'Aster Biolabs',
    industry: 'Biotechnology',
    sicCode: '8731',
    employeeCount: 128,
    city: 'Cambridge',
    state: 'MA',
    postcode: '02142',
  },
  {
    name: 'Trailhead Outdoor Brands',
    industry: 'Consumer Goods',
    sicCode: '3949',
    employeeCount: 244,
    city: 'Boulder',
    state: 'CO',
    postcode: '80301',
  },
  {
    name: 'Cobalt Data Centers',
    industry: 'Technology Infrastructure',
    sicCode: '7374',
    employeeCount: 316,
    city: 'Reno',
    state: 'NV',
    postcode: '89501',
  },
  {
    name: 'Marigold Education Group',
    industry: 'Education',
    sicCode: '8211',
    employeeCount: 720,
    city: 'Atlanta',
    state: 'GA',
    postcode: '30303',
  },
  {
    name: 'Ironwood Property Services',
    industry: 'Real Estate',
    sicCode: '6531',
    employeeCount: 176,
    city: 'Charlotte',
    state: 'NC',
    postcode: '28202',
  },
  {
    name: 'Bluepeak Telecom',
    industry: 'Telecommunications',
    sicCode: '4813',
    employeeCount: 592,
    city: 'Minneapolis',
    state: 'MN',
    postcode: '55402',
  },
];

const RISK_TIERS: RiskTier[] = ['preferred', 'standard', 'substandard'];
const LOSS_RATIO_BY_TIER: Record<RiskTier, number> = {
  preferred: 52,
  standard: 64,
  substandard: 81,
};

function pick<T>(items: T[], random: () => number): T {
  return items[Math.floor(random() * items.length)];
}

function shiftMonths(dateStr: string, months: number): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1 + months, day);
  return `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(2, '0')}-${`${date.getDate()}`.padStart(2, '0')}`;
}

function shiftDays(dateStr: string, days: number): string {
  const date = new Date(`${dateStr}T00:00:00`);
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

/** First of the month, `monthsAgo` before today. */
function monthStart(monthsAgo: number, today: string): string {
  const [year, month] = today.split('-').map(Number);
  const date = new Date(year, month - 1 - monthsAgo, 1);
  return `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(2, '0')}-01`;
}

interface TermContext {
  effectiveDate: string;
  expirationDate: string;
  writtenDate: string;
  /** Set when the term ran to expiry and was replaced by a renewal. */
  renewed: boolean;
  /** Mid-term cancellation date, if the policy did not run its full term. */
  cancelledOn: string | null;
}

function deriveStatus(term: TermContext, today: string): PolicyStatus {
  if (term.cancelledOn) return 'cancelled';
  // `PolicyStatus` has no expired state, so a term that ran to expiry without a
  // renewal is reported as cancelled — the closest of the available statuses.
  if (term.expirationDate < today) return term.renewed ? 'renewed' : 'cancelled';
  if (term.effectiveDate <= today) return 'active';
  // Not yet incepted: walk back through the pre-inception statuses by how long
  // ago the risk was written, so the pipeline has rows in every stage.
  const daysSinceWritten = Math.round(
    (new Date(`${today}T00:00:00`).getTime() - new Date(`${term.writtenDate}T00:00:00`).getTime()) /
      86_400_000
  );
  if (daysSinceWritten >= 14) return 'issued';
  if (daysSinceWritten >= 7) return 'approved';
  if (daysSinceWritten >= 2) return 'pending_underwriting';
  return 'draft';
}

/**
 * Build the full book of business: an original term per insured plus renewals,
 * endorsements and cancellations, flattened to one row per transaction.
 */
function buildRecords(today: string): PolicyReportRecord[] {
  const random = makeRandom(SEED);
  const records: PolicyReportRecord[] = [];
  let policySequence = 1000;

  INSUREDS.forEach((insured, insuredIndex) => {
    // Larger employers commonly run a smaller second program alongside the main one.
    const programCount = insured.employeeCount > 250 && random() < 0.55 ? 2 : 1;

    for (let programIndex = 0; programIndex < programCount; programIndex += 1) {
      const programScale = programIndex === 0 ? 1 : 0.3 + random() * 0.2;
      const broker = pick(BROKERS, random);
      const carrier = pick(CARRIERS, random);
      const product = pick(PRODUCTS, random);
      const riskTier = RISK_TIERS[Math.floor(random() * 3)];
      const ratePerLife = 280 + Math.round(random() * 260);
      const brokerageRate = [12.5, 15, 17.5][Math.floor(random() * 3)];
      const coverholderCommissionRate = [5, 7.5, 10][Math.floor(random() * 3)];
      const signedLinePercent = random() < 0.85 ? 100 : 85;
      const benefitPercent = [60, 66.7, 70, 80][Math.floor(random() * 4)];
      const weeklyCap = [1500, 2000, 2500, 3000][Math.floor(random() * 4)];
      const waitingPeriodDays = [0, 7, 14][Math.floor(random() * 3)];
      const taxRate = 0.0325 + random() * 0.0125;

      // Stagger inceptions across the trailing three years so the trend has depth.
      // Runs from 36 months ago through to three months ahead, so the book has
      // both a renewal history and business still working through issuance.
      const firstInceptionMonthsAgo =
        36 - Math.floor(((insuredIndex + programIndex) / (INSUREDS.length - 1)) * 39);
      let effectiveDate = monthStart(firstInceptionMonthsAgo, today);
      let previousPolicyNumber: string | null = null;
      let employeeCount = Math.max(40, Math.round(insured.employeeCount * programScale));
      let termIndex = 0;

      // Roll terms forward until the next one would incept more than a quarter out.
      while (effectiveDate <= shiftMonths(today, 3) && termIndex < 5) {
        const expirationDate = shiftDays(shiftMonths(effectiveDate, 12), -1);
        const writtenDate = shiftDays(effectiveDate, -(10 + Math.floor(random() * 25)));
        const isRenewal = termIndex > 0;

        // A term in the past either renewed or lapsed; retention runs ~85%.
        const termIsHistoric = expirationDate < today;
        const renewed = termIsHistoric && random() < 0.85;
        // Mid-term cancellations are rare and only apply to incepted terms.
        const cancelsMidTerm = !termIsHistoric && effectiveDate <= today && random() < 0.06;
        const cancelledOn = cancelsMidTerm
          ? shiftMonths(effectiveDate, 4 + Math.floor(random() * 4))
          : null;

        const term: TermContext = {
          effectiveDate,
          expirationDate,
          writtenDate,
          renewed,
          cancelledOn,
        };
        const status = deriveStatus(term, today);
        // A lapse at expiry is still a coverage end date the bordereau must carry.
        const cancellationDate =
          cancelledOn ?? (termIsHistoric && !renewed ? expirationDate : null);
        const policyId = `pol_${insuredIndex}_${programIndex}_${termIndex}`;
        const policyNumber = `PFL-${effectiveDate.slice(0, 4)}-${policySequence}`;
        policySequence += 7;

        const grossWrittenPremium = Math.round((employeeCount * ratePerLife) / 100) * 100;

        const base = {
          policyId,
          policyNumber,
          status,
          product,
          insuredName: insured.name,
          industry: insured.industry,
          sicCode: insured.sicCode,
          employeeCount,
          riskCity: insured.city,
          riskState: insured.state,
          riskPostcode: insured.postcode,
          riskCountry: 'US',
          insuredDomicile: 'US',
          brokerId: broker.id,
          brokerName: broker.name,
          brokerAgency: broker.agency,
          carrierId: carrier.id,
          carrierName: carrier.name,
          carrierNaic: carrier.naic,
          effectiveDate,
          expirationDate,
          termMonths: 12,
          brokerageRate,
          coverholderCommissionRate,
          signedLinePercent,
          aggregateLimit: weeklyCap * 12 * employeeCount,
          weeklyCap,
          benefitPercent,
          waitingPeriodDays,
          originalCurrency: 'USD',
          riskTier,
          lossRatioPct: LOSS_RATIO_BY_TIER[riskTier],
          yearOfAccount: Number(effectiveDate.slice(0, 4)),
        };

        records.push({
          ...base,
          certificateReference: `${policyNumber}-00`,
          transactionType: isRenewal ? ('renewal' as TransactionType) : ('new' as TransactionType),
          writtenDate,
          cancellationDate,
          grossWrittenPremium,
          taxesAndFees: Math.round(grossWrittenPremium * taxRate),
          reportingMonth: writtenDate.slice(0, 7),
          renewalOfPolicyNumber: previousPolicyNumber,
        });

        // Mid-term headcount additions bill as an endorsement premium.
        if (!termIsHistoric && effectiveDate <= today && random() < 0.35) {
          const addedLives = 10 + Math.floor(random() * 60);
          const endorsementEffective = shiftMonths(effectiveDate, 3);
          const endorsementPremium = Math.round((addedLives * ratePerLife * 0.75) / 50) * 50;
          records.push({
            ...base,
            certificateReference: `${policyNumber}-E1`,
            transactionType: 'endorsement',
            writtenDate: endorsementEffective,
            cancellationDate,
            grossWrittenPremium: endorsementPremium,
            taxesAndFees: Math.round(endorsementPremium * taxRate),
            reportingMonth: endorsementEffective.slice(0, 7),
            renewalOfPolicyNumber: previousPolicyNumber,
          });
        }

        // A mid-term cancellation returns the unearned premium pro rata.
        if (cancelledOn) {
          const monthsRun = 4 + Math.floor(random() * 4);
          const returnPremium = -Math.round((grossWrittenPremium * (12 - monthsRun)) / 12);
          records.push({
            ...base,
            certificateReference: `${policyNumber}-C1`,
            transactionType: 'cancellation',
            writtenDate: cancelledOn,
            cancellationDate: cancelledOn,
            grossWrittenPremium: returnPremium,
            taxesAndFees: Math.round(returnPremium * taxRate),
            reportingMonth: cancelledOn.slice(0, 7),
            renewalOfPolicyNumber: previousPolicyNumber,
          });
        }

        if (!renewed) break;

        previousPolicyNumber = policyNumber;
        effectiveDate = shiftMonths(effectiveDate, 12);
        // Renewals reprice on the current headcount, which drifts year to year.
        employeeCount = Math.max(40, Math.round(employeeCount * (0.94 + random() * 0.18)));
        termIndex += 1;
      }
    }
  });

  return records.sort((a, b) => b.writtenDate.localeCompare(a.writtenDate));
}

let cachedRecords: PolicyReportRecord[] | null = null;
let cachedForDate = '';

function allRecords(): PolicyReportRecord[] {
  const today = todayStr();
  if (!cachedRecords || cachedForDate !== today) {
    cachedRecords = buildRecords(today);
    cachedForDate = today;
  }
  return cachedRecords;
}

function delay<T>(value: T): Promise<T> {
  return new Promise(resolve => setTimeout(() => resolve(value), LATENCY_MS));
}

/** Apply the filters a real reporting endpoint would push down to SQL. */
function applyFilters(
  records: PolicyReportRecord[],
  filters: ReportingFilters
): PolicyReportRecord[] {
  return records.filter(record => {
    if (filters.fromDate && record.writtenDate < filters.fromDate) return false;
    if (filters.toDate && record.writtenDate > filters.toDate) return false;
    if (filters.products.length && !filters.products.includes(record.product)) return false;
    if (filters.carrierIds.length && !filters.carrierIds.includes(record.carrierId)) return false;
    if (filters.brokerIds.length && !filters.brokerIds.includes(record.brokerId)) return false;
    if (filters.statuses.length && !filters.statuses.includes(record.status)) return false;
    return true;
  });
}

export const mockReportingApi = {
  async fetchPolicyRecords(filters: ReportingFilters): Promise<ReportingDataResponse> {
    const records = applyFilters(allRecords(), filters);
    return delay({
      records,
      // The warehouse refreshes overnight; surfacing that keeps the numbers honest.
      generatedAt: new Date().toISOString(),
      totalCount: records.length,
    });
  },

  async fetchReferenceData(): Promise<ReportingReferenceData> {
    return delay({
      products: [...PRODUCTS],
      carriers: CARRIERS.map(c => ({ id: c.id, name: c.name })),
      brokers: BROKERS.map(b => ({ id: b.id, name: b.name, agency: b.agency })),
    });
  },
};
